import type { PaywallConfig, PaywallFeature } from '@/src/types/paywall';
import {
  billingCycleForPlan,
  buildPricingBreakdown,
  featureForPlan,
  getPersonaTrial,
  type BillingCycle,
  type CatalogPlan,
  type PricingBreakdown,
} from '@/src/features/onboarding/ownerPlanSelection';

/**
 * Pure state and derivations behind the two-phase product/price surface (#291).
 *
 * Kept out of the component so the phase rules can be asserted directly, and so
 * the same logic drives owner and driver without either screen re-deriving it.
 */

export type PricingPhase = 'product' | 'configure';

export const FEATURES: PaywallFeature[] = ['hours', 'gps', 'combo'];
export const CYCLES: BillingCycle[] = ['monthly', 'annual'];
export const MAX_LICENSES = 999;

/** Below this width the product cards stack into a single column. */
export const NARROW_BREAKPOINT = 768;

export type Persona = 'owner' | 'driver';

export const planFor = (
  catalog: CatalogPlan[],
  feature: PaywallFeature,
  billing: BillingCycle,
): CatalogPlan | null =>
  catalog.find(
    plan => featureForPlan(plan) === feature && billingCycleForPlan(plan) === billing,
  ) ?? null;

/**
 * A plan is only offered when its price can be stated honestly — that is, when
 * the breakdown builds. GPS/combo without a catalog setup fee therefore drop
 * out instead of being advertised at an incomplete price.
 */
export const isQuotable = (
  plan: CatalogPlan | null,
  monthlyPlan: CatalogPlan | null,
  feature: PaywallFeature,
  billing: BillingCycle,
): boolean => {
  if (!plan) return false;
  try {
    buildPricingBreakdown({
      plan,
      monthlyPlan,
      feature,
      billing,
      quantity: 1,
      trialDays: null,
    });
    return true;
  } catch {
    return false;
  }
};

export const quotableFeatures = (catalog: CatalogPlan[]): PaywallFeature[] =>
  FEATURES.filter(item => {
    const monthly = planFor(catalog, item, 'monthly');
    return isQuotable(monthly, monthly, item, 'monthly');
  });

/** Cycles a persona may actually BUY. Both personas SEE both (#291). */
export const purchasableCycles = (catalog: CatalogPlan[], feature: PaywallFeature): BillingCycle[] =>
  CYCLES.filter(cycle => {
    const monthly = planFor(catalog, feature, 'monthly');
    return isQuotable(planFor(catalog, feature, cycle), monthly, feature, cycle);
  });

export interface PhaseOneSummary {
  feature: PaywallFeature;
  /** Monthly plan unit amount, or null when there is no quotable monthly plan. */
  monthlyAmount: number | null;
  /**
   * What the annual plan costs PER MONTH, so the two cycles are comparable at a
   * glance without doing arithmetic in your head.
   */
  annualMonthlyEquivalent: number | null;
  /** Full amount billed once a year — never hidden behind the monthly figure. */
  annualBilledTotal: number | null;
  currency: string;
  hasSetupFee: boolean;
}

/**
 * The minimum a card needs to make phase 1 decidable: what it costs monthly,
 * what it costs monthly if paid yearly, and the real yearly charge. No
 * breakdown, no VAT repetition, no trial, no quantity.
 */
export const summariseForPhaseOne = (
  catalog: CatalogPlan[],
  feature: PaywallFeature,
): PhaseOneSummary => {
  const monthly = planFor(catalog, feature, 'monthly');
  const annual = planFor(catalog, feature, 'annual');
  const annualQuotable = isQuotable(annual, monthly, feature, 'annual');
  return {
    feature,
    monthlyAmount: monthly ? monthly.unit_amount : null,
    annualMonthlyEquivalent:
      annual && annualQuotable ? annual.unit_amount / 12 : null,
    annualBilledTotal: annual && annualQuotable ? annual.unit_amount : null,
    currency: (monthly ?? annual)?.currency ?? 'eur',
    hasSetupFee: !!monthly?.setup_fee_price_id,
  };
};

export interface ProductNaming {
  /** The product name exactly as Stripe holds it, or null when absent. */
  name: string | null;
  /** The product description from Stripe, or null when absent. */
  description: string | null;
}

/**
 * What this product is CALLED, taken from the catalog rather than local copy (#304).
 *
 * The catalog mirrors Stripe: the `product.created`/`product.updated` webhook
 * writes the local Product, and /prices/ serves its name. So this is the Stripe
 * name without a per-render call to Stripe, and renaming a product there
 * propagates on the next webhook.
 *
 * Returns null rather than a placeholder when the catalog carries no name, so
 * the caller can fall back to its own copy instead of rendering a blank label.
 */
export const productNaming = (
  catalog: CatalogPlan[],
  feature: PaywallFeature,
): ProductNaming => {
  const plan = planFor(catalog, feature, 'monthly') ?? planFor(catalog, feature, 'annual');
  const name = plan?.product?.name?.trim();
  const description = plan?.product?.description?.trim();
  return { name: name || null, description: description || null };
};

export interface PricingSelectionState {
  feature: PaywallFeature;
  billing: BillingCycle;
  quantity: number;
  plan: CatalogPlan | null;
  monthlyPlan: CatalogPlan | null;
  trial: { days: number; requiresCard: boolean } | null;
  breakdown: PricingBreakdown | null;
}

/**
 * Everything phase 2 renders, derived from the current selection.
 *
 * The driver's quantity is pinned at 1 here rather than in the UI: a licence is
 * personal, so a control suggesting otherwise would be a lie the checkout would
 * then have to contradict.
 */
export const derivePhaseTwo = ({
  catalog,
  feature,
  billing,
  quantity,
  persona,
  paywallConfig,
}: {
  catalog: CatalogPlan[];
  feature: PaywallFeature;
  billing: BillingCycle;
  quantity: number;
  persona: Persona;
  paywallConfig: PaywallConfig | null;
}): PricingSelectionState => {
  const effectiveQuantity = persona === 'driver' ? 1 : quantity;
  const plan = planFor(catalog, feature, billing);
  const monthlyPlan = planFor(catalog, feature, 'monthly');
  const quantityValid =
    Number.isInteger(effectiveQuantity) && effectiveQuantity >= 1 && effectiveQuantity <= MAX_LICENSES;
  const trial =
    plan && quantityValid
      ? getPersonaTrial(plan, feature, billing, paywallConfig, persona)
      : null;

  let breakdown: PricingBreakdown | null = null;
  if (plan && quantityValid && isQuotable(plan, monthlyPlan, feature, billing)) {
    try {
      breakdown = buildPricingBreakdown({
        plan,
        monthlyPlan,
        feature,
        billing,
        quantity: effectiveQuantity,
        trialDays: trial?.days ?? null,
      });
    } catch {
      breakdown = null;
    }
  }

  return { feature, billing, quantity: effectiveQuantity, plan, monthlyPlan, trial, breakdown };
};
